import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { modalResolver, ModalMessage, ExitCodes, ModalMessageFailure } from '../../helpers/modal/modal.helper';
import { ReactComponent as WallyWithConfetti } from '../../assets/Wally_With_Confetti.svg';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import { FormNavButtonWrapper } from '../../components/formElements/FormElements';
import Button from '../../components/button/Button';
import { inIframe } from '../../helpers';
import { SegmentProps } from '../../state-machine/actions/segment';
import { Application, Dealer } from '../../gql/graphql';
import { Trans, useTranslation } from 'react-i18next';

type Props = SegmentProps & {
  orderId: string;
  application: Application | undefined;
  dealer: Dealer;
  leaseSigned: boolean;
  loanSigned: boolean;
};

const HiveFiveStyle = styled.div`
  position: absolute;
  width: 7rem;
  left: 70%;
  bottom: -20%;
  z-index: 1;
`;
const SubTextStyle = styled.div`
  font-family: ${fontFamilies.interLight};
  font-size: 1.1rem;
  color: ${colors.darkBlue};
  width: 70%;
  line-height: 125%;
  margin: 1rem 0;
`;
const PageWrapper = styled.div`
  text-align: left;
  margin: 1rem 0;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  color: ${colors.darkBlue};
`;

const PaymentTermsSubtext = styled.div`
  color: ${colors.darkBlue};
  font-family: ${fontFamilies.interLight};
  font-size: 1rem;
  margin: 1rem 0;
  line-height: 125%;
  text-align: left;
`;

const Gradient = styled.div`
  position: absolute;
  left: -2rem;
  right: -2rem;
  width: calc(100% + 4rem);
  max-width: 465px;
  margin: 0 auto;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0) 100%);
  margin-bottom: 2rem;
  height: 50px;
`;

const LeaseSigned: React.FC<Props> = ({ orderId, application, dealer, leaseSigned, loanSigned, track }) => {
  const { t } = useTranslation();
  const domains = dealer.domains!;

  const handleCloseModal = () => {
    const payload: ModalMessage = {
      wcfSuccess: true,
      closeModal: true,
      payload: {
        reason: 'modal closed',
        reasonCode: ExitCodes.generalCloseModal,
        orderId: orderId,
      } as ModalMessageFailure,
    };
    track('Lease Complete');
    modalResolver(payload, domains);
  };

  useEffect(() => {
    const sendSuccessEvent = () => {
      const payload: ModalMessage = {
        wcfSuccess: true,
        closeModal: false,
        payload: { orderId: orderId },
      };

      modalResolver(payload, domains);
    };

    // despite its name, this page is used for leases and loans
    if (application?.lease) {
      track('Lease Agreement Signed');
    } else {
      track('Loan Agreement Signed');
    }

    //if app status is not in docssigned
    if (loanSigned || leaseSigned) {
      sendSuccessEvent();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageWrapper>
      <h1 style={{ fontSize: '3.5rem' }}>
        <Trans i18nKey={($) => $.pageLeaseSigned.allDone} components={{ br: <br /> }} />
      </h1>
      <ImageWrapper>
        <SubTextStyle>{t(($) => $.pageLeaseSigned.thanks)}</SubTextStyle>
        <HiveFiveStyle>
          <WallyWithConfetti title={t(($) => $.pageLeaseSigned.leaseSigned)} />
        </HiveFiveStyle>
      </ImageWrapper>
      <Gradient />
      <PaymentTermsSubtext>
        {t(($) => $.pageLeaseSigned.paymentTerms, {
          appMode: t(($) => $.pageLeaseSigned.lease),
        })}
        {inIframe() && t(($) => $.pageLeaseSigned.redirectNotice)}
      </PaymentTermsSubtext>
      {inIframe() && (
        <FormNavButtonWrapper>
          <Button onClick={handleCloseModal}>{t(($) => $.buttonLabels.continue)}</Button>
        </FormNavButtonWrapper>
      )}
    </PageWrapper>
  );
};

export default LeaseSigned;
